import { Header } from '@/components/layout/Header';
import { ProfilePopup } from '@/components/my-profile/ProfilePopup';
import { RoutingMembersDropdown } from '@/components/my-profile/RoutingMembersDropdown';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { useAppSelector } from '@/store/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProfileInfoCard } from '@/components/my-profile/ProfileInfoCard';
import { AgentQueueStatusLive } from '@/components/my-profile/AgentQueueStatusLive';
import { api } from '@/store/api';
import type { User } from '@/types/user';
import type { Manager } from '@/types/auth';
import { useAmazonConnectAgent } from '@/hooks/useAmazonConnectAgent';
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Box,
  CircularProgress,
  Stack,
  List,
  ListItem,
  ListItemText,
} from '@gapartners/ui';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const {
  useGetManagerQuery,
  useGetMyRoutingProfileUsersQuery,
  useGetMyRoutingProfileQueuesQuery,
} = api;
import {
  AgentAverageWaitTimeSection,
  AgentServiceLevelSection,
  AgentAverageTalkTimeSection,
  AgentAverageHoldTimeSection,
} from '@/components/agent-performance';
import { QueueStatusWidget } from '@/components/dashboard/QueueStatusWidget';

export function MyProfilePage() {
  const { t } = useTranslation();
  const { user } = useAppSelector(state => state.auth);
  const { agentState } = useAmazonConnectAgent();
  const [popupUser, setPopupUser] = useState<User | Manager | null>(null);
  const [metricsExpanded, setMetricsExpanded] = useState(false);

  const { data: manager } = useGetManagerQuery();
  const { data: routingData, isLoading: isRoutingLoading } =
    useGetMyRoutingProfileUsersQuery();
  const [queuesExpanded, setQueuesExpanded] = useState(false);
  const { data: queuesData, isFetching: isQueuesLoading } =
    useGetMyRoutingProfileQueuesQuery(undefined, { skip: !queuesExpanded });

  usePageMetadata({
    title: t('pages.myProfile.title'),
    description: t('pages.myProfile.description'),
  });
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />

      {/* Page Header */}
      <Paper
        variant="outlined"
        square
        sx={{
          px: 0,
          py: { xs: 1, sm: 1.25 },
          bgcolor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderLeft: 0,
          borderRight: 0,
          borderTop: 0,
          borderRadius: 0,
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h5"
            fontWeight={600}
            color="text.primary"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 0.5 }}
          >
            {t('pages.myProfile.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
            {t('pages.myProfile.description')}
          </Typography>
        </Container>
      </Paper>

      <Container
        maxWidth="xl"
        sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          <ProfileInfoCard
            title={t('pages.myProfile.profileInfo.title')}
            idName={t('pages.myProfile.agentId')}
            name={user!.name}
            email={user!.email}
            role={user!.jobTitle || ''}
            idValue={user!.id || ''}
            status={agentState}
            routingProfileName={routingData?.routingProfileName || ''}
            onAvatarClick={() => setPopupUser(user)}
          />
          <ProfileInfoCard
            title={t('pages.team.manager')}
            idName={t('pages.myProfile.managerId')}
            name={manager?.displayName || ''}
            email={manager?.mail || ''}
            role={manager?.jobTitle || ''}
            idValue={manager?.id || ''}
            status={manager?.status || ''}
            routingProfileName={routingData?.routingProfileName || ''}
            onAvatarClick={() =>
              manager && setPopupUser({ ...manager, isManager: true })
            }
          />
        </Box>

        {/* Routing Members Accordion */}
        <Box sx={{ mt: { xs: 3, sm: 4 }, width: '100%' }}>
          <Accordion variant="outlined" sx={{ width: '100%', borderRadius: 2 }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="routing-members-content"
              id="routing-members-header"
              sx={{ px: { xs: 2, sm: 3 } }}
            >
              <Typography fontWeight={500} variant="body1">
                {t('pages.myProfile.showMyRoutingMembers')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 0 }}>
              {isRoutingLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <RoutingMembersDropdown members={routingData?.users || []} />
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* My Queues Accordion */}
        <Box sx={{ mt: { xs: 3, sm: 4 }, width: '100%' }}>
          <Accordion
            variant="outlined"
            sx={{ width: '100%', borderRadius: 2 }}
            expanded={queuesExpanded}
            onChange={(_, isExpanded) => setQueuesExpanded(isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="my-queues-content"
              id="my-queues-header"
              sx={{ px: { xs: 2, sm: 3 } }}
            >
              <Typography fontWeight={500} variant="body1">
                {t('pages.myProfile.assignedQueues')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 0 }}>
              {isQueuesLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress />
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ width: '100%', overflow: 'hidden' }}
                >
                  {!queuesData?.queues?.length ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ px: 2, py: 3, textAlign: 'center' }}
                    >
                      {t('pages.myProfile.noQueuesFound')}
                    </Typography>
                  ) : (
                    <List
                      disablePadding
                      sx={{ maxHeight: 288, overflow: 'auto' }}
                    >
                      {queuesData.queues.map(queue => (
                        <ListItem key={queue.queueId}>
                          <ListItemText
                            primary={queue.queueName}
                            secondary={queue.channels.join(', ')}
                            primaryTypographyProps={{ fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Performance Metrics Accordion */}
        <Box sx={{ mt: { xs: 3, sm: 4 }, width: '100%' }}>
          <Accordion
            variant="outlined"
            sx={{ width: '100%', borderRadius: 2 }}
            expanded={metricsExpanded}
            onChange={(_, isExpanded) => setMetricsExpanded(isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="performance-metrics-content"
              id="performance-metrics-header"
              sx={{ px: { xs: 2, sm: 3 } }}
            >
              <Typography fontWeight={500} variant="body1">
                {t('pages.agent.performanceMetrics.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pt: 2 }}>
              {metricsExpanded ? (
                <Stack spacing={3} sx={{ width: '100%' }}>
                  <AgentQueueStatusLive />
                  <QueueStatusWidget />
                  {/* <AgentQueueStatusSection /> */}
                  <AgentAverageWaitTimeSection />
                  <AgentServiceLevelSection />
                  <AgentAverageTalkTimeSection />
                  <AgentAverageHoldTimeSection />
                </Stack>
              ) : null}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>

      {popupUser && (
        <ProfilePopup
          popupUser={popupUser}
          user={user}
          onClose={() => setPopupUser(null)}
        />
      )}
    </Box>
  );
}
